import {Component, EventEmitter, Input, Output, OnInit} from '@angular/core';
import {ReportSectionEnum} from '../../../../models/measure/report';
import {PdfExportService} from '../../../services/export-pdf/pdf-export.service';
import {TranslateService} from '@ngx-translate/core';
import {GaugeCircleChartData} from '../../../../models/influencer';
import { performingInfluencerSortList, BestPerformingInfluencerSortBy } from '../../../app.constants';
import { AppSettings } from '../../../utils/app-settings';

@Component({
  selector: 'measure-content',
  templateUrl: './measure-content.component.html',
  styleUrls: ['./measure-content.component.scss']
})
export class MeasureContentComponent implements OnInit {

  @Input()
  title: string;

  @Input()
  period: number;

  @Input()
  data: any;

  @Input()
  pageIndex: number;

  @Input()
  moduleId: number;

  @Input()
  platformList: any[];

  // benchmark performance active module id
  @Input()
  bpIndex: number;

  // channel oppotunity active module id
  @Input()
  coIndex: number;

  // benchmark performance active module id
  @Input()
  removedTopics: string[];

  @Input()
  industryDrillDownReportPieCenter: [number];

  @Input()
  industryDrillDownReportPieSize: string;

  @Input()
  numberItemInRoiReport: number;

  @Input()
  numberItemForInteractionReport: number;

  @Input()
  numberItemForOonaReport: number;

  @Input()
  segmentInfluencerOverviewPieChartCenter: [number];

  @Input()
  selectedSortType: string;

  @Output() reportDataChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() reportSettingsChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() selectSorting: EventEmitter<any> = new EventEmitter<any>();

  // Variables for best performing influencer sorting
  sortList = [];
  isEnableBestPerformingInfluencerSorting: boolean;

  constructor(private pdfService: PdfExportService, private translate: TranslateService) {
  }

  ngOnInit() {
    this.sortList = performingInfluencerSortList;
    this.isEnableBestPerformingInfluencerSorting = AppSettings.isEnableBestPerformingInfluencerSorting();
  }

  get isSelectedRoiReport() {
    return this.moduleId === ReportSectionEnum.ROI_REPORT;
  }

  get isSelectedTimeReport() {
    return this.moduleId === ReportSectionEnum.TIMELINE_REPORT;
  }

  get isSelectedKPIReport() {
    return this.moduleId === ReportSectionEnum.KPI_REPORT;
  }

  get isSelectedLocalAreaReport() {
    return this.moduleId === ReportSectionEnum.LOCAL_AREAS_REPORT;
  }

  get isSelectedMentionTimelineReport() {
    return this.moduleId === ReportSectionEnum.MENTION_TIMELINE_REPORT;
  }

  get isSelectedExposureKpiReport() {
    return this.moduleId === ReportSectionEnum.EXPOSURE_KPI_REPORT;
  }

  get isSelectedExposurePlalformReport() {
    return this.moduleId === ReportSectionEnum.EXPOSURE_PLATFORM_REPORT;
  }

  get isSelectedBestExposureReport() {
    return this.moduleId === ReportSectionEnum.BEST_EXPOSURE_REPORT;
  }

  get isSelectedSegmentationInfluencerOverviewReport() {
    return this.moduleId === ReportSectionEnum.SEGMENTATION_INFLUENCER_OVERVIEW_REPORT;
  }

  get isSelectedSegmentationSegmentReport() {
    return this.moduleId === ReportSectionEnum.SEGMENTATION_SEGMENT_REPORT;
  }

  get isSelectedSegmentationIndustryReport() {
    return this.moduleId === ReportSectionEnum.SEGMENTATION_INDUSTRY_REPORT;
  }

  get isSelectedInsightOpportunityReport() {
    return this.moduleId === ReportSectionEnum.INSIGHT_OPPORTUNITY_REPORT;
  }

  get isSelectedInsightBenchmarkReport() {
    return this.moduleId === ReportSectionEnum.INSIGHT_BENCHMARK_REPORT;
  }

  get isSelectedBestPerformingReport() {
    return this.moduleId === ReportSectionEnum.BEST_PERFORMING_INFLUENCER_REPORT;
  }

  get isSelectedInteractionReport() {
    return this.moduleId === ReportSectionEnum.INTERACTION_REPORT;
  }

  get isSelectedOonaReport() {
    return this.moduleId === ReportSectionEnum.OONA_REPORT;
  }

  get titleTooltip() {
    switch (this.moduleId) {
      case ReportSectionEnum.INTERACTION_REPORT:
        return this.translate.instant('info_box.measure.interaction_title');
      case ReportSectionEnum.BEST_PERFORMING_INFLUENCER_REPORT:
        return this.translate.instant('info_box.measure.bpi_title');
      case ReportSectionEnum.ROI_REPORT:
        return this.translate.instant('info_box.measure.roi_title');
      case ReportSectionEnum.OONA_REPORT:
        return this.translate.instant('info_box.measure.post_benchmark');
      case ReportSectionEnum.TIMELINE_REPORT:
        return this.translate.instant('info_box.measure.performance_timeline');
      case ReportSectionEnum.MENTION_TIMELINE_REPORT:
        return this.translate.instant('info_box.measure.mention_timeline');
      case ReportSectionEnum.SEGMENTATION_INFLUENCER_OVERVIEW_REPORT:
        return this.translate.instant('info_box.measure.influencer_count');
      default:
        return null;
    }
  }

  hasTransparentBackground(): boolean {
    return this.isSelectedKPIReport || this.isSelectedExposureKpiReport || this.isSelectedSegmentationIndustryReport;
  }

  onChangeReportData(evt) {
    this.reportDataChange.emit(evt);
  }

  onChangeSetting(evt) {
    this.reportSettingsChange.emit(evt);
  }

  getActivePlatformReach(platformReach: GaugeCircleChartData[]) {
    return platformReach.map((pr) => {
      const platform = this.platformList.find(p => p.name.toLowerCase() === pr.name.toLowerCase());
      return platform ? platform.id : null;
    });
  }

  shouldDisplayTitle() {
    if (!this.pageIndex) {
      return this.title;
    }
    // in pdf mode, should not show title for duplicated page if no data
    if (this.isSelectedInteractionReport) {
      return this.pageIndex === 0 || (this.pageIndex > 0 && this.data.posts.length > 0);
    } else if (this.isSelectedOonaReport) {
      return this.pageIndex === 0 || (this.pageIndex > 0 && this.data.data.length > 0);
    } else if (this.isSelectedRoiReport) {
      return this.pageIndex === 0 || (this.pageIndex > 0 && this.data.datas.length > 0);
    } else {
      return this.title;
    }
  }

  onSelectSorting(evt) {
    this.selectSorting.emit(evt.id);
  }

  get isSortedByReach() {
    return this.selectedSortType === BestPerformingInfluencerSortBy.REACH;
  }

}



// WEBPACK FOOTER //
// ./src/app/main/measure/measure-content/measure-content.component.ts